import { useState } from "react";
import { confirmDialog } from "../components/ConfirmDialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Check, X, Trash2, Clock, Star, MessageSquare } from "lucide-react";

const STATUS_META = {
  pending: { label: "En attente", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30", icon: Clock },
  approved: { label: "Publié", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30", icon: Check },
  rejected: { label: "Rejeté", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30", icon: X },
};

const TIER_META = {
  free: { label: "Gratuit", cls: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30" },
  essentiel: { label: "Essentiel", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  pro: { label: "Pro", cls: "bg-sky-500/10 text-sky-300 border-sky-500/30" },
  max: { label: "Max", cls: "bg-violet-500/10 text-violet-300 border-violet-500/30" },
};

function StarBar({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`w-3.5 h-3.5 ${i < rating ? "text-amber-400 fill-amber-400" : "text-zinc-700"}`} />
      ))}
    </div>
  );
}

export default function AdminReviewsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("pending");

  const { data: reviews = [], isLoading } = useQuery({
    queryKey: ["admin-reviews"],
    queryFn: async () => (await api.get("/admin/reviews")).data,
  });

  const modMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/admin/reviews/${id}`, { status })).data,
    onSuccess: (_, { status }) => {
      toast.success(status === "approved" ? "Avis publié" : "Avis rejeté");
      qc.invalidateQueries({ queryKey: ["admin-reviews"] });
      qc.invalidateQueries({ queryKey: ["reviews"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/reviews/${id}`)).data,
    onSuccess: () => {
      toast.success("Supprimé");
      qc.invalidateQueries({ queryKey: ["admin-reviews"] });
      qc.invalidateQueries({ queryKey: ["reviews"] });
    },
  });

  const counts = reviews.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});
  const filtered = filter === "all" ? reviews : reviews.filter((r) => r.status === filter);

  return (
    <div className="mx-auto max-w-5xl px-6 py-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2">Admin</div>
        <h1 className="font-display font-black text-3xl">Avis à modérer</h1>
        <p className="mt-2 text-sm text-zinc-400">
          Les avis ne sont pas publiés automatiquement. Approuvez-les avant qu'ils apparaissent sur la page d'accueil.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-reviews-filters">
        {[
          { id: "pending", label: `En attente (${counts.pending || 0})` },
          { id: "approved", label: `Publiés (${counts.approved || 0})` },
          { id: "rejected", label: `Rejetés (${counts.rejected || 0})` },
          { id: "all", label: `Tous (${reviews.length})` },
        ].map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`admin-reviews-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-lg text-xs uppercase tracking-[0.15em] border transition-colors ${
              filter === f.id
                ? "bg-emerald-500 text-black border-emerald-500"
                : "border-white/10 text-zinc-400 hover:border-white/20 hover:text-zinc-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-zinc-500">Chargement…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center bg-card border-white/5">
          <MessageSquare className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500">
            {filter === "pending" ? "Aucun avis en attente. ✨" : "Aucun avis dans cette catégorie."}
          </p>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="admin-reviews-list">
          {filtered.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <Card key={r.id} className="p-4 bg-card border-white/5" data-testid={`admin-review-${r.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-medium text-zinc-200">{r.pseudonym || "Anonyme"}</span>
                      <StarBar rating={r.rating} />
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.15em] ${meta.cls}`}
                      >
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                      {(() => {
                        const t = TIER_META[r.user_tier] || TIER_META.free;
                        return (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.15em] ${t.cls}`}
                            data-testid={`admin-review-tier-${r.id}`}
                            title="Abonnement de l'auteur"
                          >
                            {t.label}
                          </span>
                        );
                      })()}
                    </div>
                    <p className="mt-2 text-sm text-zinc-300 whitespace-pre-line">{r.comment}</p>
                    <div className="mt-2 text-[11px] text-zinc-500">
                      {r.email} · {r.created_at ? new Date(r.created_at).toLocaleString("fr-FR") : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.status !== "approved" && (
                      <Button
                        size="sm"
                        className="bg-emerald-500 text-black hover:bg-emerald-300"
                        onClick={() => modMut.mutate({ id: r.id, status: "approved" })}
                        data-testid={`admin-review-approve-${r.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" /> Publier
                      </Button>
                    )}
                    {r.status !== "rejected" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-white/10 bg-transparent hover:bg-white/5"
                        onClick={() => modMut.mutate({ id: r.id, status: "rejected" })}
                        data-testid={`admin-review-reject-${r.id}`}
                      >
                        <X className="w-4 h-4 mr-1" /> Rejeter
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-rose-400 hover:bg-rose-500/10"
                      onClick={async () => (await confirmDialog({ title: "Supprimer définitivement cet avis ?", description: "Action définitive." })) && delMut.mutate(r.id)}
                      data-testid={`admin-review-delete-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
